import { RESULT_TYPE, COMPLIANCE_TYPE } from 'src/components/JobSpellCheckPopup/types';

export const updateResultsWithNewResult = (array, newResult) => array.map(
  result => result.id === newResult.id ? newResult : result
);

/* Filters clean checklist/datasheet spellcheck array from ignored words */
export const generateCombinedArray = (preparedDataArray, spellcheckedArray, type) => {
  const combinedArray = [];

  spellcheckedArray.forEach((spellcheckCollection, index) => {
    spellcheckCollection.forEach(spellCheck => {
      combinedArray.push({
        data: preparedDataArray[index].data,
        result: preparedDataArray[index].result,
        spellCheck: {
          ...spellCheck,
          replaceValue: spellCheck.suggestions[0] ?? ''
        },
        type,
      });
    });
  });

  return combinedArray;
};

export const getActiveResultQuestionText = (selectedResult, tranlations, datasheets) => {
  if (!selectedResult || !datasheets) {
    return null;
  }

  if (selectedResult.type === RESULT_TYPE.DATASHEET) {
    // eslint-disable-next-line prefer-destructuring
    const text = datasheets
      // result datasheet
      .find(datasheet => datasheet.id === selectedResult.data.datasheetId).currentVersion.datasheetSections
      // result section
      .find(section => section.questions.some(question => question.datasheetQuestionId === selectedResult.data.datasheetQuestionId)).questions
      // result question
      .find(question => question.datasheetQuestionId === selectedResult.data.datasheetQuestionId).datasheetQuestion.currentVersion.text;

    if(selectedResult.result.type === 'comments') {
      return `${text} - Comments`;
    }

    return text;
  }

  switch (selectedResult.result.type) {
    case COMPLIANCE_TYPE.DETAILED_FINDING: {
      return tranlations('FORM_DETAILED_FINDING');
    }
    case COMPLIANCE_TYPE.LEGAL_REQUIREMENTS: {
      return tranlations('FORM_LEGAL_REQUIREMENTS');
    }
    case COMPLIANCE_TYPE.RECOMMENDATIONS: {
      return tranlations('FORM_RECOMMENDATIONS');
    }
    case COMPLIANCE_TYPE.CORRECTIVE_ACTION_TAKEN: {
      return tranlations('FORM_CORRECTIVE_ACTION_TAKEN');
    }
    case 'comments': {
      return tranlations('COMMENTS');
    }
    default: {
      return selectedResult.result.type;
    }
  }
};

export const getActiveResultTextValue = (selectedResult) => {
  if (!selectedResult) {
    return '';
  }

  return selectedResult.result.value;
};

/* Transform checklist results into spellcheck array needed for popup WITH ALL RESULTS */
export const getChecklistSpellCheckArray = (arr) => {
  const resultsArray = [];

  arr.forEach(question => {
    const { checklistId, checklistQuestionId, checklistVersion, currentQuestionId, id: resultId } = question;
    const data = {
      checklistId,
      checklistQuestionId,
      checklistVersion,
      currentQuestionId,
      resultId,
    };

    if (question.nonCompliances.length) {
      question.nonCompliances.forEach(nonCompliance => {
        const { detailedFinding, legalRequirements, recommendations, id } = nonCompliance;

        if (detailedFinding) {
          resultsArray.push({
            data,
            result: {
              type: COMPLIANCE_TYPE.DETAILED_FINDING,
              value: detailedFinding,
              isNonCompliance: true,
              findingId: id,
            }
          });
        }
        if (legalRequirements) {
          resultsArray.push({
            data,
            result: {
              type: COMPLIANCE_TYPE.LEGAL_REQUIREMENTS,
              value: legalRequirements,
              isNonCompliance: true,
              findingId: id,
            }
          });
        }
        if (recommendations) {
          resultsArray.push({
            data,
            result: {
              type: COMPLIANCE_TYPE.RECOMMENDATIONS,
              value: recommendations,
              isNonCompliance: true,
              findingId: id,
            }
          });
        }
      });
    }

    if(question.correctiveActionTaken) {
      resultsArray.push({
        data,
        result: {
          type: COMPLIANCE_TYPE.CORRECTIVE_ACTION_TAKEN,
          value: question.correctiveActionTaken,
          isNonCompliance: true,
        }
      });
    }

    if (question.comments) {
      resultsArray.push({
        data,
        result: {
          type: 'comments',
          value: question.comments,
          isNonCompliance: false,
        }
      });
    }
  });

  return resultsArray;
};

/* Transform datasheet results into spellcheck array needed for popup WITH ALL RESULTS */
export const getDatasheetSpellCheckArray = (arr) => {
  const resultsArray = [];

  arr.forEach(question => {
    const { datasheetId, datasheetQuestionId, datasheetVersion, currentQuestionId, id: resultId } = question;
    const data = {
      datasheetId,
      datasheetQuestionId,
      datasheetVersion,
      currentQuestionId,
      resultId,
    };

    if(question.result?.type === 'text' && question.result?.value) {
      resultsArray.push({
        data,
        result: {
          type: 'result',
          value: question.result.value,
        }
      });
    }

    if (question.comments) {
      resultsArray.push({
        data,
        result: {
          type: 'comments',
          value: question.comments,
        }
      });
    }
  });

  return resultsArray;
};

/* Gets all the words to check from clean checklist spellcheck results array */
export const getChecklistWordsArray = (arr) => arr.map(result => result.result.value);

/* Gets all the words to check from clean datasheet spellcheck results array */
export const getDatasheetWordsArray = (arr) => arr.map(result => result.result.value);

export const getNewChecklistStoredResult = (selectedResult, storedResult) => {
  const newStoredResult = { ...storedResult };
  const currentWord = selectedResult.spellCheck.word;
  const newWord = selectedResult.spellCheck.replaceValue || '';

  if (selectedResult.result.isNonCompliance) {
    newStoredResult.nonCompliances = newStoredResult.nonCompliances.map(finding => {
      const newFinding = { ...finding };

      if (newFinding.checklistQuestionResultId === selectedResult.data.resultId) {
        const replaceRegex = new RegExp(`${ currentWord }`,'g');
        newFinding[selectedResult.result.type] = newFinding[selectedResult.result.type] && newFinding[selectedResult.result.type].replace(replaceRegex, newWord);
      }

      return newFinding;
    });
  }

  const replaceRegex = new RegExp(`${ currentWord }`,'g');
  newStoredResult[selectedResult.result.type] = newStoredResult[selectedResult.result.type].replace(replaceRegex, newWord);

  return newStoredResult;
};

export const getNewTextValueFromResult = (selectedResult, replaceValue) => {
  const currentWord = selectedResult.spellCheck.word;
  const newWord = replaceValue || selectedResult.spellCheck.replaceValue || '';

  const currentValue = selectedResult.result.value;
  const replaceRegex = new RegExp(`${ currentWord }`,'g');

  return currentValue.replace(replaceRegex, newWord) || '';
};

/* gets array of words without ignored ones */
export const getWordsArrayWithoutIgnored = (spellCheckArr, ignoresArr) => spellCheckArr
  .map((result) => result
    .filter((word) => !ignoresArr
      .some(ignoredWord => ignoredWord.word === word.word)
    )
  );

export const getChecklistNonCompliancesSummaryByType = (result, type) => result?.nonCompliances
  .filter(nonCompliance => nonCompliance[type])
  .map(nonCompliance => nonCompliance[type])
  .join('\n\n');
